/**
 * A parser of the server-sent event format for the chat stream of agent-api. The browser
 * `EventSource` cannot send an `Authorization` header, so the client reads the stream with
 * `fetch` and gives the text to this parser. pos-agent `docs/chat-assistant-v1.md` owns the
 * event format.
 */

export interface SseMessage {
  /** The `id` field of this event, or null. A live text delta has no id. */
  id: string | null;
  /** The `event` field, or "message" when the event has none. */
  event: string;
  /** The `data` lines of the event, joined with a newline. */
  data: string;
}

export interface SseParser {
  push(chunk: string): void;
  /** The stream ended. An event without its blank line is incomplete, and it is dropped. */
  end(): void;
}

export function createSseParser(onMessage: (message: SseMessage) => void): SseParser {
  let buffer = '';
  // A chunk that ends with CR can be the first half of a CRLF.
  let skipLeadingLf = false;
  let id: string | null = null;
  let event = '';
  let data: string[] = [];

  const reset = () => {
    id = null;
    event = '';
    data = [];
  };

  const dispatch = () => {
    if (data.length > 0) onMessage({ id, event: event || 'message', data: data.join('\n') });
    reset();
  };

  const readLine = (line: string) => {
    if (line === '') {
      dispatch();
      return;
    }
    // A comment, for example the ": ping" that keeps the connection open.
    if (line.startsWith(':')) return;

    const colon = line.indexOf(':');
    const field = colon === -1 ? line : line.slice(0, colon);
    let value = colon === -1 ? '' : line.slice(colon + 1);
    if (value.startsWith(' ')) value = value.slice(1);

    if (field === 'data') data.push(value);
    else if (field === 'event') event = value;
    else if (field === 'id' && !value.includes('\u0000')) id = value;
  };

  return {
    push(chunk: string) {
      // An empty chunk must not end the wait for the LF of a CRLF.
      if (chunk === '') return;
      buffer += chunk;
      if (skipLeadingLf && buffer.startsWith('\n')) buffer = buffer.slice(1);
      skipLeadingLf = false;

      let start = 0;
      for (let index = 0; index < buffer.length; index++) {
        const char = buffer[index];
        if (char !== '\n' && char !== '\r') continue;
        readLine(buffer.slice(start, index));
        if (char === '\r') {
          if (index + 1 === buffer.length) skipLeadingLf = true;
          else if (buffer[index + 1] === '\n') index++;
        }
        start = index + 1;
      }
      buffer = buffer.slice(start);
    },

    end() {
      buffer = '';
      reset();
    },
  };
}

/** Reads a response body to its end and gives each event to `onMessage`. */
export async function readSseStream(
  body: ReadableStream<Uint8Array>,
  onMessage: (message: SseMessage) => void,
): Promise<void> {
  const parser = createSseParser(onMessage);
  const reader = body.getReader();
  // The decoder keeps the bytes of a character that a chunk boundary splits.
  const decoder = new TextDecoder();
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      parser.push(decoder.decode(value, { stream: true }));
    }
    parser.push(decoder.decode());
  } finally {
    parser.end();
    reader.releaseLock();
  }
}
